import { statusChipClass, statusFillClass } from "@/lib/status-tone";
import { cn } from "@/lib/utils";

/**
 * A status chip — a coloured dot, then the status token itself in mono.
 *
 * Colours follow the DESIGN §6 lifecycle, not decoration: amber is waiting on a
 * human, green has been through review, violet is folded into a cluster, red failed,
 * sky is mid-pipeline, grey is out of the bank. A reviewer scanning the queue reads
 * the colour before the word.
 *
 * Every status gets a chip, including the transient ones. The alternative — a chip
 * only for states that want a decision, and a bare dot for the machine states — was
 * considered and rejected: while a run is in flight, *where* a question stopped is
 * worth reading at a glance, and that is exactly what the bare-dot version made you
 * read the word for.
 *
 * The literal token is printed, never a prettified label, so grepping a DESIGN status
 * name lands in the code that renders it.
 */

/**
 * The dot without the chip, for a control that is already a chip — the status facets
 * on Questions are buttons, and nesting a chip inside one gives a row of boxes in
 * boxes with two competing pressed states.
 */
export function StatusDot({ value, className }: { value: string; className?: string }) {
  return (
    <span
      className={cn("size-1.5 shrink-0 rounded-full", statusFillClass(value), className)}
      aria-hidden="true"
    />
  );
}

export function Badge({ value, className }: { value: string | null | undefined; className?: string }) {
  if (!value) return <span className="text-bank-muted">—</span>;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 font-mono text-xs font-medium whitespace-nowrap",
        statusChipClass(value),
        className,
      )}
    >
      <span className={cn("size-1.5 shrink-0 rounded-full", statusFillClass(value))} aria-hidden="true" />
      {value}
    </span>
  );
}
